import axios from 'axios'

import config from '@/config'
import { getHeaders } from '@/Services/ApiService'

export type TaskImportSource = {
  provider: 'ms_project' | 'excel'
  format: 'mpp' | 'gantt' | 'pull_planning'
  fileName: string
  importedAt: string
  sheetName?: string
  sheetVisibility?: 'visible' | 'hidden' | 'veryHidden'
  sourceRow?: number
  raw: Record<string, unknown>
}

export type ProjectImportFormat = 'ms_project' | 'gantt' | 'pull_planning'

export type ProjectImportSheet = {
  name: string
  index: number
  visibility: 'visible' | 'hidden' | 'veryHidden'
  format: 'gantt' | 'pull_planning' | 'unknown'
  headers: string[]
  rowCount: number
}

export type ProjectImportColorCode = {
  id: string
  sourceValue: string
  name: string
  color: string
  sourceSheet: string
  firstSourceRow: number
}

export type MsProjectPreviewTask = {
  uid: string
  id: string
  name: string
  type: 'partida' | 'trabajo' | 'hito' | 'reunion'
  start: string | null
  finish: string | null
  percentComplete: number
  parentUid: string | null
  parentId: string | null
  parentName: string | null
  summary: boolean
  colorCodeId?: string | null
  importSource: TaskImportSource
}

export type MsProjectPreviewGroup = {
  uid: string
  id: string
  name: string
  outlineLevel: number
  parentUid: string | null
  parentId: string | null
  parentName: string | null
  itemCount: number
  importSource: TaskImportSource
}

export type MsProjectPreviewRelation = {
  fromUid: string
  fromName: string
  fromId: string
  toUid: string
  toName: string
  toId: string
  type: string
  lag: number
  importSource: TaskImportSource
}

export type MsProjectPreviewRestriction = {
  taskUid: string
  taskId: string
  taskName: string
  description: string
  date: string | null
  importSource: TaskImportSource
}

export type MsProjectPreviewCost = {
  taskUid: string
  taskId: string
  taskName: string
  resourceUid: string
  description: string
  type: string
  unit: string
  crewNumber: number
  quantity: number
  unitPrice: number
  estimatedCost: number
  importSource: TaskImportSource
}

export type MsProjectPreviewValidationError = {
  code: string
  scope: 'project' | 'task' | 'color_code'
  taskUid?: string
  taskId?: string
  taskName?: string
  colorCodeId?: string
  message: string
}

export type MsProjectBaselineTask = {
  taskUid: string
  start: string | null
  finish: string | null
  durationMinutes?: number
  workMinutes?: number
  cost?: number
}

export type MsProjectBaseline = {
  position: number
  isEarnedValueBaseline: boolean
  tasks: MsProjectBaselineTask[]
}

export type MsProjectPreview = {
  sourceType?: ProjectImportFormat
  selectedSheet?: string
  sheets?: ProjectImportSheet[]
  project: {
    name: string
    shortName: string
    startsAt: string | null
    endsAt: string | null
  }
  summary: {
    fileName: string
    fileSize: number
    taskCount: number
    summaryTaskCount?: number
    groupCount: number
    itemCount: number
    workCount: number
    milestoneCount: number
    relationCount: number
    unsupportedRelationCount: number
    restrictionCount: number
    resourceCount: number
    costCount: number
    totalEstimatedCost: number
  }
  validation: {
    isValid: boolean
    errors: MsProjectPreviewValidationError[]
    warnings?: MsProjectPreviewValidationError[]
  }
  tasks: MsProjectPreviewTask[]
  groups: MsProjectPreviewGroup[]
  relations: MsProjectPreviewRelation[]
  restrictions: MsProjectPreviewRestriction[]
  costs: MsProjectPreviewCost[]
  baselines: MsProjectBaseline[]
  colorCodes?: ProjectImportColorCode[]
  archivePayload?: {
    xml: string
    parsed: Record<string, any>
  }
}

export type MsProjectImportResult = {
  project: any
  taskList: any
  summary: MsProjectPreview['summary']
  /**
   * True when the backend short-circuited because the same file (by SHA-256)
   * was already imported by this user. The returned `project` is the existing
   * one; no new project was created.
   */
  deduplicated?: boolean
  archive?: {
    id: string
    status: string
  }
}

/**
 * Response of POST /ms-project-imports/preview.
 *
 * When `deduplicated` is true the backend found a completed archive for the
 * same file (by SHA-256) owned by this user and skipped parsing entirely. The
 * `preview` payload is a minimal echo of the existing project, which is enough
 * to tell the user where they will land.
 */
export type MsProjectPreviewResponse = {
  importId: string
  expiresAt: string | null
  preview: MsProjectPreview
  deduplicated?: boolean
}

export type MsProjectTaskCorrection = {
  uid: string
  name?: string
  start?: string | null
  finish?: string | null
  exclude?: boolean
}

const postMsProjectFile = async (path: 'preview' | 'confirm', file: File, corrections?: MsProjectTaskCorrection[]) => {

  const url = `${config.apiHost}/ms-project-imports/${path}`
  const formData = new FormData()
  formData.append('file', file)
  if (corrections?.length) formData.append('corrections', JSON.stringify(corrections))

  const headers = getHeaders()

  const response = await axios.post(url, formData, {
    headers: {
      ...headers,
      'Content-Type': 'multipart/form-data',
    },
  })

  if (!response.data.success) {

    throw new Error(response.data.error)
  }

  return response.data.data
}

const postMsProjectConfirm = async (importId: string, corrections?: MsProjectTaskCorrection[]) => {

  const url = `${config.apiHost}/ms-project-imports/confirm`
  const formData = new FormData()
  formData.append('importId', importId)
  if (corrections?.length) formData.append('corrections', JSON.stringify(corrections))

  const response = await axios.post(url, formData, {
    headers: {
      ...getHeaders(),
      'Content-Type': 'multipart/form-data',
    },
  })

  if (!response.data.success) {

    throw new Error(response.data.error)
  }

  return response.data.data as MsProjectImportResult
}

export const previewMsProjectImport = async (file: File) => (
  postMsProjectFile('preview', file) as Promise<MsProjectPreviewResponse>
)

/**
 * Confirm a previously previewed import using the `importId` returned by
 * previewMsProjectImport. The file is not re-uploaded: the backend reads the
 * persisted session, which avoids re-parsing the .mpp.
 */
export const confirmMsProjectImport = async (importId: string, corrections: MsProjectTaskCorrection[] = []) => (
  postMsProjectConfirm(importId, corrections)
)

export const previewExcelProjectImport = async (file: File, sheetName?: string): Promise<{ preview: MsProjectPreview }> => {
  const formData = new FormData()
  formData.append('file', file)
  if (sheetName) formData.append('sheetName', sheetName)
  const response = await axios.post(`${config.apiHost}/excel-project-imports/preview`, formData, {
    headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
  })
  if (!response.data.success) throw new Error(response.data.error)
  return response.data.data
}

export const confirmExcelProjectImport = async (preview: MsProjectPreview): Promise<MsProjectImportResult> => {
  const response = await axios.post(`${config.apiHost}/excel-project-imports/confirm`, { preview }, { headers: getHeaders() })
  if (!response.data.success) throw new Error(response.data.error)
  return response.data.data
}
